import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Button, EmptyState, failedEmptyState } from "../../../components/ui";
// The PNG rather than the SVG beside it: the panel is a rendered gradient with
// a glass object on it, which is what that file is a vector wrapper around —
// half a megabyte of embedded bitmap either way, and the PNG is the one that
// arrives at the resolution it was authored at.
import trialArt from "../../../assets/svg/free trial/free plan image.png";
import { ROUTES, trialDetailsPath } from "../../../routes/paths";
import { formatPrice } from "../../brand/planFormat";
import CycleToggle, { CycleOption } from "../components/CycleToggle";
import TrialLayout from "../components/TrialLayout";
import { CYCLE_LABELS } from "../data/subscriptionPlans";
import { useCountUp } from "../hooks/useCountUp";
import { usePlans } from "../hooks/usePlans";
import { useTrialAccess } from "../hooks/useTrialAccess";
import { TRIAL_DAYS } from "../trialState";
import type { BillingCycleId, SubscriptionPlan } from "../types/billing.types";

/**
 * Which cycle the switch opens on.
 *
 * The month, not the year the Billing tab recommends. This screen is the first
 * thing a brand new account sees, and the cheaper commitment is the one that
 * reads as an offer rather than as a sale; the switch is right there, and the
 * yearly saving is written on it.
 */
const OPENS_ON: BillingCycleId = "monthly";

/**
 * What committing to a year takes off a month of it, in percent.
 *
 * Worked out from the two prices rather than written down, so nothing that
 * quotes it can outlive a re-pricing — which `CYCLE_PRICE_NOTES` can, and says
 * so. Two things quote it: the switch, as `(-10%)`, and the note under the
 * yearly price. One source for both, so the page cannot claim two figures for
 * one discount.
 *
 * `null` where there is nothing honest to claim: one of the two cycles missing,
 * a month priced at nothing, or a year that saves nothing against it.
 */
const yearlyDiscountPercent = (plans: SubscriptionPlan[]): number | null => {
  const monthly = plans.find((plan) => plan.id === "monthly");
  const yearly = plans.find((plan) => plan.id === "yearly");
  if (!monthly?.pricePerMonth || !yearly?.pricePerMonth) return null;

  const percent = Math.round(
    (1 - yearly.pricePerMonth / monthly.pricePerMonth) * 100
  );

  return percent > 0 ? percent : null;
};

/**
 * What the launch offer takes off a month, in rupees.
 *
 * The *monthly* plan's cut, whichever cycle is on screen, and that is the whole
 * point of the function. The year's own gap to its list price is ₹260 — the
 * ₹200 launch cut and the 10% for committing to a year, already added together
 * — so quoting it here would say the ₹60 twice: once in this chip and again in
 * the "Additional 10% off" beside it, for a row that claims ₹320 off a ₹260
 * discount. The two chips have to be the two cuts in the order they come off,
 * and the first of them is the one the month is sold at.
 *
 * Falls back to the plan in hand where there is no monthly to read the launch
 * price from. That is the same case `yearlyDiscountPercent` returns `null` in,
 * so there is no second chip for this one to double-count against, and the full
 * gap is then the only honest thing to call it.
 *
 * `null` where the cycle is sold at its list price and there is nothing to
 * announce.
 */
const launchFlatOff = (
  plans: SubscriptionPlan[],
  plan: SubscriptionPlan
): number | null => {
  const launch = plans.find((candidate) => candidate.id === "monthly") ?? plan;
  const off = launch.listPricePerMonth - launch.pricePerMonth;

  return off > 0 ? off : null;
};

/**
 * The first screen a new account sees: one plan, two cycles, and fourteen days
 * of it for nothing.
 *
 * ## Why this is not the Billing tab
 *
 * The same product is on sale in Settings › Billings, as two cards side by
 * side. Here it is one card and a switch, because the question is different:
 * that screen asks a customer which cycle to buy, this one asks somebody who
 * has just signed up whether to start at all. So the cycles collapse into a
 * control, the picture takes the other half of the screen, and the button
 * starts a trial rather than opening a checkout.
 *
 * Every figure is the price list's — `GET /api/v1/billing/plans/`, through
 * `usePlans`, the same call the Billing tab draws its cards with. Nothing here
 * writes a price down, and the discount on the switch is derived from the two
 * that arrive. A list that fails to load draws no card at all: an invented
 * price is worse than an honest "we could not load this".
 *
 * No sidebar: this screen is outside `AppLayout` on purpose. The rail appears
 * for the first time on the dashboard, once the trial has actually started.
 */
const TrialPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const priceList = usePlans();

  // Whether this workspace is being shown the offer at all — the server's
  // `can_start_trial`, off `GET /billing/subscription/`. See the guard below.
  const gate = useTrialAccess();

  const [cycle, setCycle] = useState<BillingCycleId>(OPENS_ON);

  const plan =
    priceList.plans.find((candidate) => candidate.id === cycle) ?? null;

  const yearlyPercent = yearlyDiscountPercent(priceList.plans);

  /**
   * The price on the card, counting rather than jumping when the switch moves.
   *
   * The two cycles are two prices for one product, and swapping the figure
   * outright reads as the card being replaced by another card — the eye has
   * nothing to follow, so the only way to see what the switch did is to have
   * memorised the number before pressing it. Counted, the figure is visibly the
   * *same* price being re-worked, and the direction of travel says which way
   * the choice went before the number has finished arriving.
   *
   * The site's pricing section does exactly this with the same two figures, and
   * this screen is where its "Subscribe now" leads; see `useCountUp` for the
   * duration and ease it borrows.
   *
   * Called above the guards, because hooks cannot be. `null` while there is no
   * plan — which is every render the guards below return early on — and the
   * first real price is seeded rather than counted up to from zero.
   */
  const countedPrice = useCountUp(plan?.pricePerMonth ?? null);

  // Both cycles on the switch whether or not the list has arrived — the words
  // are this app's own (`CYCLE_LABELS`), and a control that appears a moment
  // after the card it sits on would move the price as it lands. The saving in
  // the brackets is the one part that waits for figures.
  const options: CycleOption[] = [
    { id: "monthly", label: CYCLE_LABELS.monthly },
    {
      id: "yearly",
      label: `${CYCLE_LABELS.yearly}${
        yearlyPercent === null ? "" : ` (-${yearlyPercent}%)`
      }`,
    },
  ];

  /**
   * What the launch offer takes off a month, where one is running.
   *
   * The struck-through price beside the figure says the same thing in two
   * numbers; this says it in words, which is what makes it read as an offer
   * with an end rather than as a permanent price. The figure is the launch cut
   * itself rather than this cycle's whole gap to its list price — see
   * `launchFlatOff` for why those are not the same number on the year.
   */
  const flatOff = plan ? launchFlatOff(priceList.plans, plan) : null;

  /**
   * The second discount, on the cycle that has one.
   *
   * The year is sold at the launch price *and* a further cut for committing to
   * a year, and neither the struck-through figure nor the flat-off line beside
   * it can say which of the two is which — by the time it reaches the price it
   * is one number. This says the second one out loud, on the cycle it applies
   * to, as the third chip in the row under the price.
   *
   * "Additional", because that is what it is: the flat chip before it has
   * already been counted, and this is what comes off on top of it — which only
   * reads true because that chip is the launch cut and not the year's whole
   * gap to its list price. The percent is the same figure the switch quotes,
   * and it is worked out from the two *discounted* prices, so it is the cut
   * that lands after the flat one rather than a second slice of the list price — `CYCLE_PRICE_NOTES` says the same thing in a constant,
   * and that is the copy that can go stale.
   */
  const yearlyNote =
    plan?.id === "yearly" && yearlyPercent !== null
      ? `Additional ${yearlyPercent}% off`
      : null;

  /**
   * Nothing is drawn until the price list has answered.
   *
   * Not a placeholder, and not the card half-built: either of those is a box
   * of one height standing where a box of another height is about to go, and
   * this screen is centred in the window — so the shorter thing sits in the
   * middle and then *opens* to full height as the figures land, which is the
   * one movement there is no excuse for on a page somebody is reading.
   *
   * The card's height cannot be known before the plan is, either: it is the
   * price, the chips under it and the feature list that decide it. So the wait
   * shows nothing and says so, and the first thing painted is the finished
   * screen at its own full height.
   */
  const deciding = gate.status === "loading";

  /**
   * The offer is over before it is drawn: this workspace may not start a trial.
   *
   * Because it is already paying, because a trial is running, or because it has
   * had one — the server says which in `access_source`, and none of the three
   * is a reason to show somebody fourteen free days they cannot have. Straight
   * to the app instead, with `replace` so Back does not land here again.
   *
   * Asked *before* the price list is waited on, and answered as soon as it
   * lands: this screen is where every way into the app now points, so a
   * subscriber signing in passes through it, and there is no sense making them
   * wait on the price of a plan they are not being shown.
   *
   * A failed read is deliberately *not* treated as a refusal. Not knowing is
   * not the same as knowing they have had their trial, and the same caution the
   * subscription gate takes over the create buttons applies here: the worst a
   * wrongly-shown offer costs is a refusal at the last step, while a wrongly-
   * hidden one costs somebody the trial they were entitled to.
   */
  if (gate.status === "ready" && !gate.access?.canStartTrial) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  if (deciding || priceList.status === "loading") {
    return (
      <TrialLayout showLogo={false}>
        <p role="status" className="sr-only">
          Loading the plan…
        </p>
      </TrialLayout>
    );
  }

  /**
   * No plan to show: the list failed, or it came back without the cycle the
   * switch is on — a cycle that has stopped being sold. Both are the same thing
   * to the reader, and neither is a page to invent a price for.
   */
  if (!plan) {
    return (
      <TrialLayout showLogo={false}>
        <EmptyState
          {...failedEmptyState(
            "We could not load the plan",
            <Button size="sm" variant="secondary" onClick={priceList.reload}>
              Try again
            </Button>
          )}
        />
      </TrialLayout>
    );
  }

  // No mark in the corner: the artwork below carries the wordmark itself, and
  // two of them on one screen is one logo too many.
  //
  // Held to the width the two panels were drawn at rather than taking the page
  // the way the step after it does: that one is a screenful of fields, which
  // read better long, while this is a picture beside a price and neither has
  // anything to do with the extra room.
  return (
    <TrialLayout showLogo={false}>
      {/* What this screen is, in the reader's terms rather than the product's:
          they have just signed up and are one press from being in. It is the
          page's own heading, so it sits above both panels rather than inside
          the card — the picture is as much a part of the offer as the price. */}
      {/* The deepest of the brand's teals — the one the refund promise and the
          plan card's "Active" pill are set in, rather than the mid teal a link
          takes. A heading this size in the lighter shade reads as something to
          press.

          Every size on this screen steps up 2px at `min-[1400px]`, and not
          before: below that the card is already as wide as it should be, so
          larger type would only cost it lines. The step is written on each
          class rather than kept in the config, because it is this screen's
          decision and nothing else in the app shares it. */}
      <h1 className="text-[26px] min-[1400px]:text-[28px] 3xl:text-[32px] font-semibold leading-tight text-[#00424B]">
        You&rsquo;re one step away
      </h1>

      {/* Twelve columns from `lg` up — five of artwork against seven of offer,
          which is the ratio it was drawn at. Stacked below that, picture first,
          so the page opens on the offer at every width.

          `items-stretch`, which is the grid's own default and is left unstated
          for that reason: it is what makes the two columns start and finish on
          the same two lines however tall the card grows — at any width, in any
          language, and whatever the price list turns out to hold. */}
      <div className="mt-6 3xl:mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12 3xl:gap-8">
        {/* The artwork, and nothing over it: the wordmark and both lines are
            drawn into the file, so this is the whole panel rather than a
            background to caption.

            A background rather than an `<img>`, which is what lets it fill a
            box whose height is decided by the card beside it — a replaced
            element would either keep its own aspect and leave a gap, or need a
            height nobody can know in advance. `cover` and centred: the panel is
            all but square, so the trim at these proportions is a few pixels off
            each side, well inside the margin the sentence is drawn with.

            Below `lg` there is no card beside it to take a height from, so it
            holds its own square. `role="img"` with the words on it, since a
            background has no `alt` and the sentence is part of the offer. */}
        <div
          role="img"
          aria-label="Naurist - your whole practice, in one place. Present like the expert you are!"
          // Quoted, and that is load-bearing: the file is `free plan image.svg`
          // and webpack keeps the spaces in the URL it emits. An `<img src>`
          // takes those unescaped, but an unquoted CSS `url(...)` does not — it
          // parses as a syntax error and the panel comes out blank.
          style={{ backgroundImage: `url("${trialArt}")` }}
          className="aspect-square rounded-[6px] bg-cover bg-center lg:aspect-auto lg:col-span-5"
        />

        {/* The offer. */}
        <section
          aria-labelledby="trial-plan-heading"
          className="flex flex-col rounded-lg border border-[#5BD3E1] bg-[#F8FEFF] p-6 sm:p-7 3xl:p-8 lg:col-span-7"
        >
          <h2 id="trial-plan-heading" className="sr-only">
            Start your free trial
          </h2>

          {/* Every part of the card is drawn at once, because by here the plan
              is known — the screen does not render at all until it is. There is
              no loading branch inside the card and no placeholder standing in
              for one; see the guards above the return. */}
              {/* The saving and the switch on one line: what this cycle is
                  worth, and the control that changes it. Wrapping rather than
                  shrinking, since the badge is a figure and the switch is a
                  target. */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                {plan.saving ? (
                  <span className="rounded-full border border-brand-dark bg-white px-3 py-1 text-[11px] min-[1400px]:text-[13px] 3xl:text-[14px] font-semibold uppercase tracking-wide text-brand-dark">
                    Save {formatPrice(plan.saving)}
                  </span>
                ) : (
                  // Holds the row's left-hand place so the switch does not
                  // slide across when a cycle with nothing to boast about is
                  // picked.
                  <span aria-hidden="true" />
                )}

                <CycleToggle
                  options={options}
                  value={cycle}
                  onChange={setCycle}
                />
              </div>

              {/* Baseline-aligned, so the small print sits on the line the big
                  figure stands on rather than against its middle — the same row
                  `PlanCard` draws. */}
              <div className="mt-5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {/* `tabular-nums`, which matters only while it is counting:
                    the digits in between are ones nobody chose, and at
                    proportional widths they would shove the struck-through
                    price beside them back and forth for the length of the
                    tween. Fixed-width figures hold the row still and let the
                    number be the only thing moving. */}
                <span className="text-[34px] sm:text-[40px] min-[1400px]:text-[42px] 3xl:text-[48px] font-bold leading-none text-ink tabular-nums">
                  {formatPrice(countedPrice ?? plan.pricePerMonth)}
                </span>
                {/* Struck through by eye; said in words to a screen reader,
                    which announces an `<s>` inconsistently and would otherwise
                    read the old price as a second, contradictory one. */}
                <span className="sr-only">, was </span>
                <s className="text-[20px] min-[1400px]:text-[22px] 3xl:text-[26px] font-medium text-ink-subtle">
                  {formatPrice(plan.listPricePerMonth)}
                </s>
                <span className="text-[15px] min-[1400px]:text-[17px] 3xl:text-[18px] text-ink-muted">
                  /month
                </span>
                <span className="text-[12px] min-[1400px]:text-[14px] 3xl:text-[15px] text-ink-muted">
                  + GST
                </span>
              </div>

              {(flatOff || yearlyNote) && (
                // Violet, alone on this card — the teals here are the brand and
                // its recommendation, and a saving inside a saving said in a
                // third teal would read as another piece of the same claim
                // rather than as the aside it is. The same colour and the same
                // reasoning as `PlanCard`'s price note.
                //
                // One line for both discounts, bullet-separated, because they
                // are two parts of one answer to "why is this price not the
                // struck-through one" — the launch cut, and the year's own on
                // top of it. Wrapping rather than shrinking: at a narrow card
                // the second part drops to its own line and still reads.
                <p className="mt-2 text-[13px] min-[1400px]:text-[15px] 3xl:text-[16px] text-[#6234CC]">
                  {flatOff && (
                    <>
                      Launch offer
                      <span aria-hidden="true" className="px-2">
                        &bull;
                      </span>
                      Flat {formatPrice(flatOff)} off
                    </>
                  )}
                  {flatOff && yearlyNote && (
                    <span aria-hidden="true" className="px-2">
                      &bull;
                    </span>
                  )}
                  {yearlyNote}
                </p>
              )}

              <p className="mt-6 3xl:mt-7 text-[14px] min-[1400px]:text-[16px] 3xl:text-[17px] font-medium text-ink">
                One plan. Complete access
              </p>

              <ul className="mt-4 flex flex-col gap-3 3xl:gap-4">
                {plan.features.map((feature) => (
                  <li key={feature.name} className="flex gap-2">
                    {/* Nudged down to the cap height of the line beside it —
                        centred on the line box it would sit a shade high. */}
                    <Check
                      aria-hidden="true"
                      strokeWidth={2.5}
                      className="mt-[3px] h-4 w-4 3xl:h-[18px] 3xl:w-[18px] flex-shrink-0 text-brand"
                    />
                    <p className="text-[13px] min-[1400px]:text-[15px] 3xl:text-[16px] leading-[1.55] text-ink-muted">
                      <span className="font-medium text-ink">
                        {feature.name}
                      </span>
                      {" - "}
                      {feature.description}
                    </p>
                  </li>
                ))}
              </ul>

              {/* Pushed to the foot of the card, so the button sits on the
                  panel's baseline however long the feature list runs. */}
              <div className="mt-auto pt-8 3xl:pt-9">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => navigate(trialDetailsPath(plan.id))}
                  // The cycle is named on the thing that starts it, not only in
                  // the switch above — the visible words stay in front, so
                  // anyone driving the page by voice can ask for what they read.
                  aria-label={`Start ${TRIAL_DAYS} days free trial of ${plan.productName} ${plan.cycleLabel}`}
                >
                  Start {TRIAL_DAYS} days free trial
                </Button>
              </div>
        </section>
      </div>
    </TrialLayout>
  );
};

export default TrialPlanPage;
